import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Grid, Typography, Button } from "@material-ui/core";
import CircularProgress from "@mui/material/CircularProgress";
import { store } from "../index";
import { order_place, clear_last_order } from "../store/actions/action";
import { clearCart } from "../store/actions/cartActions";
import {
  buildSaveOrderPayload,
  create_order,
} from "../utils/order_helper";
import {
  BUTTON_COLOR,
  PAYMENTS_STATUS,
  PAYMENT_STATE,
  TOKEN_KEY,
  STORE_DETAILS,
} from "../utils/constant";

const PaymentAck = () => {
  console.log("[PaymentAck] RENDER", { at: new Date().toISOString() });

  const navigate = useNavigate();
  const [message, setMessage] = useState("Verifying your payment...");
  const [checking, setChecking] = useState(true);

  const cartReducer = useSelector((state) => state.cartReducer) || {};
  const sessionReducer = useSelector((state) => state.sessionReducer) || {};
  const customerReducer = useSelector((state) => state.customerReducer) || {};
  const urlParams = new URLSearchParams(window.location.search);
  const effectiveOfflineInvoiceNo =
    cartReducer.offline_invoice_no ||
    localStorage.getItem("offline_invoice_no") ||
    urlParams.get("merchantTransactionId") ||
    urlParams.get("offline_invoice_no") ||
    urlParams.get("transactionId") ||
    "";

  if (effectiveOfflineInvoiceNo && !cartReducer.offline_invoice_no) {
    store.dispatch({ type: "SET_OFFLINE_INVOICE_NO", offline_invoice_no: effectiveOfflineInvoiceNo });
  }

  const token =
    sessionReducer?.token ||
    localStorage.getItem(TOKEN_KEY) ||
    localStorage.getItem("jwt") ||
    "";

  const storeDetailsObj = JSON.parse(localStorage.getItem(STORE_DETAILS) || "{}");
  const resolvedStoreId =
    sessionReducer?.store_details?.store_id ||
    sessionReducer?.store_id ||
    storeDetailsObj?.store_id ||
    storeDetailsObj?.store?.store_id ||
    "";

  const primaryColor =
    sessionReducer?.store_config?.general_config?.primary_color || BUTTON_COLOR;

  console.log("[PaymentAck] STATE SNAPSHOT", {
    offline_invoice_no: effectiveOfflineInvoiceNo,
    has_token: !!token,
    token_length: token ? token.length : 0,
    token_first_10: token ? token.slice(0, 10) : null,
    isAuthented: sessionReducer?.isAuthented,
    is_fsr: sessionReducer?.is_fsr,
    store_id: resolvedStoreId,
    has_store_details: !!sessionReducer?.store_details,
    has_store_config: !!sessionReducer?.store_config,
    has_selectedTender: !!cartReducer?.selectedTender,
    addedItems_count: cartReducer?.addedItems?.length || 0,
    table_details: sessionReducer?.table_details,
    active_table_orders_keys: Object.keys(customerReducer?.active_table_orders || {}),
    primaryColor,
    checking_state: checking,
    message_state: message,
  });

  const orderPlacedKey = effectiveOfflineInvoiceNo
    ? `payment_ack_placed:${effectiveOfflineInvoiceNo}`
    : null;

  const checkPaymentStatus = useCallback(async () => {
    console.log("[PaymentAck] checkPaymentStatus START", {
      offline_invoice_no: effectiveOfflineInvoiceNo,
      orderPlacedKey,
      already_placed_flag: orderPlacedKey ? sessionStorage.getItem(orderPlacedKey) : null,
    });

    if (!effectiveOfflineInvoiceNo) {
      console.warn("[PaymentAck] no offline_invoice_no → redirect /checkout");
      navigate("/checkout", { replace: true });
      return;
    }

    if (orderPlacedKey && sessionStorage.getItem(orderPlacedKey) === "1") {
      console.log("[PaymentAck] order already placed for this invoice → navigate /order-status");
      localStorage.removeItem("offline_invoice_no");
      navigate("/order-status");
      return;
    }

    setChecking(true);
    setMessage("Verifying your payment...");

    let statusResp;
    try {
      console.log("[PaymentAck] POST /payments/status →", {
        url: PAYMENTS_STATUS,
        body: { offline_invoice_no: effectiveOfflineInvoiceNo, store_id: resolvedStoreId },
        has_token: !!token,
      });
      const { data } = await axios.post(
        PAYMENTS_STATUS,
        { offline_invoice_no: effectiveOfflineInvoiceNo, store_id: resolvedStoreId },
        {
          headers: {
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json",
            "x-api-source": "webapp",
            authorization: token || "",
          },
          timeout: 8000,
        }
      );
      statusResp = data;
      console.log("[PaymentAck] POST /payments/status ✓", { statusResp });
    } catch (err) {
      console.error("[PaymentAck] POST /payments/status ✗", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      if (err?.response?.status === 401) {
        console.warn("[PaymentAck] 401 → redirect /rescan");
        window.location.href = "/rescan";
        return;
      }
      setMessage("Payment status could not be verified. Returning to payment screen...");
      setChecking(false);
      setTimeout(() => {
        navigate("/checkout", { replace: true });
      }, 1200);
      return;
    }

    const paymentState =
      statusResp?.data?.payment_state ||
      statusResp?.payment_state ||
      statusResp?.data?.state ||
      statusResp?.state;

    console.log("[PaymentAck] paymentState resolved", { paymentState });

    if (paymentState === PAYMENT_STATE.FAILED || paymentState !== PAYMENT_STATE.SUCCESS) {
      console.warn("[PaymentAck] payment failed or non-success → redirect /checkout", { paymentState });
      localStorage.removeItem("offline_invoice_no");
      setMessage("Payment failed or was cancelled. Returning to payment screen...");
      setChecking(false);
      setTimeout(() => {
        navigate("/checkout", { replace: true });
      }, 1200);
      return;
    }

    setMessage("Payment successful. Placing your order...");

    const isFSR = sessionReducer?.is_fsr === true;
    const tableNo = String(
      sessionReducer?.table_details?.table_id ||
        sessionReducer?.table_details?.table_no ||
        ""
    ).trim();
    const activeOrder = isFSR
      ? customerReducer?.active_table_orders?.[tableNo]?.order || null
      : null;

    console.log("[PaymentAck] order-placement branch", {
      isFSR,
      tableNo,
      has_activeOrder: !!activeOrder,
      branch: isFSR && activeOrder ? "FSR_UPDATE" : "CREATE_ORDER",
    });

    try {
      const selectedTender = cartReducer?.selectedTender || {};

      console.log("[PaymentAck] → buildSaveOrderPayload");
      const payload = buildSaveOrderPayload({ activeOrder });
      console.log("[PaymentAck] ← buildSaveOrderPayload", {
        store_id: payload?.store_id,
        child_store_id: payload?.child_store_id,
        transaction_grand_amount: payload?.transaction_grand_amount,
        item_count: payload?.item_details?.length,
        offline_invoice_no: payload?.offline_invoice_no,
      });

      const dataObj = statusResp?.data || {};
      const paymentGatewayResponse = dataObj.paymentGatewayResponse || {};
      const paymentDetailsFirst = dataObj.paymentDetails?.[0] || {};
      const splitInstrumentFirst = paymentDetailsFirst.splitInstruments?.[0] || {};
      const rail = splitInstrumentFirst.rail || {};

      const cardLastFourDigit = paymentGatewayResponse.cardLastFourDigit || "";
      const cardHolderName = paymentGatewayResponse.payerName || paymentGatewayResponse.customerName || "";
      const transactionId = paymentDetailsFirst.transactionId || dataObj.orderId || "";

      payload.payment_details = [{
        amount: payload?.transaction_grand_amount || 0,
        amount_round: 0,
        change: 0,
        isInternetAvailable: true,
        payment_gateway: "Payment Gateway",
        tnd_code: selectedTender.tnd_code || "QR",
        tnd_des: selectedTender.tnd_des || "UPI",
        tnd_type: selectedTender.tnd_type || "UPI",
        transType: "SALE",
        transaction_detail: [
          {
            amount: 0,
            Change: 0,
            enteredAmount: 0,
            name: "",
            payeeName: "",
            paymentIntent: [],
            paymentMethodId: -1,
            paymentResponseData: {
              abstractPaymentStatus: "",
              acquirerCode: "",
              acquisitionId: "",
              acquisitionKey: "",
              additionalAmount: 0,
              amount: paymentDetailsFirst.amount ? (paymentDetailsFirst.amount * 100) : (dataObj.amount ? (dataObj.amount * 100) : 0),
              amountAdditional: 0,
              amountCashBack: 0,
              amountOriginal: 0,
              applicationId: "",
              applicationType: "",
              apps: [],
              authCode: paymentGatewayResponse.authCode || "",
              batchNumber: "",
              callTC: false,
              callbackEnabled: false,
              cardHolderCurrencyExponent: 0,
              cardLastFourDigit: cardLastFourDigit,
              cardTxnTypeDesc: "",
              cardType: paymentGatewayResponse.cardType || "",
              chargeSlipDate: "",
              createdTime: 0,
              currencyCode: "",
              customerEmail: "",
              customerName: paymentGatewayResponse.customerName || "",
              customerNameAvailable: false,
              customerReceiptUrl: "",
              dccOpted: false,
              deviceSerial: "",
              displayPAN: "",
              dxMode: "",
              enableRki: false,
              externalDevice: false,
              externalRefNumber: paymentGatewayResponse.externalRefNumber || rail.utr || "",
              formattedPan: "",
              invoiceNumber: "",
              issuerCode: "",
              latitude: "",
              longitude: "",
              manualRefund: false,
              maximumPayAttemptsAllowed: 0,
              maximumSuccessfulPaymentAllowed: 0,
              merchantCode: "",
              merchantName: "",
              message: "",
              messageCode: "",
              mid: paymentGatewayResponse.mid || "",
              middlewareStanNumber: 0,
              nameOnCard: "",
              noExpiryFlag: false,
              nonceStatus: "",
              onlineRefundable: false,
              orderNumber: "",
              orgCode: "",
              otpRequired: false,
              p2pRequestId: "",
              payerName: paymentGatewayResponse.payerName || "",
              paymentCardBin: "",
              paymentCardBrand: "",
              paymentCardType: "",
              paymentGateway: paymentGatewayResponse.paymentGateway || "",
              paymentMode: paymentGatewayResponse.paymentMode || paymentDetailsFirst.paymentMode || "",
              pgInvoiceNumber: "",
              postingDate: 0,
              processCode: "",
              processCronOutput: false,
              readableChargeSlipDate: "",
              realCode: "",
              receiptUrl: "",
              redirect: false,
              refundable: false,
              reload: false,
              reverseReferenceNumber: "",
              rrNumber: "",
              sessionKey: "",
              settlementStatus: "",
              shiftNo: 0,
              shouldRoundOffConvertedAmount: false,
              shouldRoundOffExchangeRate: false,
              signReqd: false,
              signable: false,
              signatureId: "",
              stan: "",
              states: [],
              status: "",
              success: false,
              tid: paymentGatewayResponse.tid || "",
              tipAdjusted: false,
              tipEnabled: false,
              totalAmount: 0,
              transactionCertificate: "",
              tsi: "",
              tvr: "",
              twoStepConfirmPreAuth: false,
              txnId: paymentGatewayResponse.txnId || transactionId,
              txnMetadata: [],
              txnType: "",
              txnTypeDesc: "",
              userAgreement: "",
              userMobile: "",
              username: "",
              voidable: false
            },
            payment_type: "Payment Gateway",
            percent: 0,
            printData: [],
            status: false,
            terminalInvoiceNo: "",
            tnd_code: "",
            tnd_des: "",
            tnd_type: "",
            transactionDetail: [],
            transaction_summary: {
              acc_number: "******" + cardLastFourDigit,
              aid: "",
              app_lab: "",
              atc: "",
              auth_code: paymentGatewayResponse.authCode || "",
              card_holder_name: cardHolderName,
              card_type: paymentGatewayResponse.cardType || "",
              customer_vpa: rail.vpa || "",
              date_time: paymentDetailsFirst.timestamp ? new Date(paymentDetailsFirst.timestamp).toISOString() : "",
              merchant_id: paymentGatewayResponse.mid || "",
              payment_mode: paymentGatewayResponse.paymentMode || paymentDetailsFirst.paymentMode || "",
              payment_status: paymentState,
              ref_number: paymentGatewayResponse.externalRefNumber || rail.utr || "",
              tc: "",
              terminal_id: "",
              tid: paymentGatewayResponse.tid || "",
              transaction_log_id: transactionId,
              tsi: "",
              tvr: ""
            }
          }
        ],
        transaction_id: transactionId || ""
      }];

      console.log("[PaymentAck] → create_order");
      const saveResp = await create_order(payload);
      console.log("[PaymentAck] ← create_order", { saveResp });

      if (saveResp?.status) {
        if (orderPlacedKey) sessionStorage.setItem(orderPlacedKey, "1");
        localStorage.removeItem("offline_invoice_no");
        let orderData = saveResp.data;
        if (orderData && !Array.isArray(orderData)) {
          orderData = orderData.master ? [orderData.master] : [orderData];
        }
        store.dispatch(order_place(orderData));
        if (isFSR && activeOrder) {
          store.dispatch(clear_last_order());
          store.dispatch({
            type: "CLEAR_ACTIVE_TABLE_ORDER",
            payload: { tableNo },
          });
        }
        store.dispatch(clearCart());
        navigate("/order-status");
      } else {
        setMessage("Could not place order. Returning to payment screen...");
        setChecking(false);
        setTimeout(() => {
          navigate("/checkout", { replace: true });
        }, 1500);
      }
    } catch (error) {
      console.error("[PaymentAck] order-placement catch", {
        message: error?.message,
        stack: error?.stack,
      });
      setMessage("Could not place order. Returning to payment screen...");
      setChecking(false);
      setTimeout(() => {
        navigate("/checkout", { replace: true });
      }, 1500);
    }
  }, [
    effectiveOfflineInvoiceNo,
    orderPlacedKey,
    token,
    resolvedStoreId,
    navigate,
    sessionReducer,
    customerReducer,
    cartReducer?.selectedTender,
  ]);

  const checkPaymentStatusRef = useRef(checkPaymentStatus);
  useEffect(() => {
    checkPaymentStatusRef.current = checkPaymentStatus;
  }, [checkPaymentStatus]);

  useEffect(() => {
    console.log("[PaymentAck] MOUNT effect fired — calling checkPaymentStatus");
    checkPaymentStatusRef.current();

    const handlePageShow = (event) => {
      if (event.persisted) {
        console.warn("[bfcache] PaymentAck restored from back-forward cache → redirecting to /checkout");
        window.location.replace("/checkout");
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    // Safety fallback timer: guarantee the loader NEVER gets stuck spinning indefinitely
    const safetyTimer = setTimeout(() => {
      console.warn("[PaymentAck] Safety timeout reached — redirecting to /checkout");
      setMessage("Returning to payment screen...");
      setChecking(false);
      setTimeout(() => {
        navigate("/checkout", { replace: true });
      }, 800);
    }, 6000);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      clearTimeout(safetyTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid
      style={{
        height: "100dvh",
        backgroundColor: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      {checking && (
        <CircularProgress
          style={{ color: primaryColor }}
          size={48}
          thickness={4}
        />
      )}
      <Typography
        style={{
          marginTop: "24px",
          fontFamily: "Satoshi",
          fontSize: "18px",
          fontWeight: 700,
          color: primaryColor,
          textAlign: "center",
        }}
      >
        {message}
      </Typography>
      <Typography
        style={{
          marginTop: "8px",
          fontFamily: "Satoshi",
          fontSize: "14px",
          color: "#4B4A4B",
          textAlign: "center",
        }}
      >
        Please do not close or refresh this page.
      </Typography>
      <Button
        variant="contained"
        onClick={checkPaymentStatus}
        disabled={checking}
        style={{
          marginTop: "24px",
          backgroundColor: primaryColor,
          color: "#ffffff",
          fontSize: "16px",
          fontWeight: 700,
          fontFamily: "Satoshi",
          borderRadius: "8px",
          padding: "12px 24px",
          textTransform: "none",
          boxShadow: "none",
          opacity: checking ? 0.6 : 1,
        }}
      >
        Check Payment Status
      </Button>
    </Grid>
  );
};

export default PaymentAck;
